import { useEffect, useState, useRef, useMemo } from "react";
import { Stack, useTheme } from "@mui/material";
import { Post } from "src/api/client-api/post/postSchema";
import { SsqSsqHeading } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { filterHeadingsByHiddenElements, getVisibleSsqSsqHeadingSsqProperties } from "src/utils/ssq";
import { useUpdateProperty, UpdateProperty } from "src/api/client-api/property/useUpdateProperty";
import { calculationFailureMessage, classifyCalculationFailure } from "src/api/client-api/post/calculationFailure";
import { useDeleteProperty, DeleteProperty } from "src/api/client-api/property/useDeleteProperty";
import { useFormBaseContext } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { HeadingHelperTextBox } from "./HeadingHelperTextBox";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { OlapScoresResult } from "src/api/client-api/olapScores/olapScoresSchema";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { DEFAULT_SCORE_PRECISION } from "src/context/Scores/provider";
import {
    ValidationMessage,
    ValidationMessagesByKey,
} from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { buildPropertyValidationKey, extractValidationMessages } from "src/utils/validation";
import { useLocation } from "react-router";
import { PostUsedInActionPlanModal } from "src/components/QuestionnairesList/PostUsedInActionPlanModal.tsx";
import { DataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { unifyHeadingsAndProperties } from "src/components/QuestionnaireForm/context/utils/unifyHeadingsAndProperties";
import { UnifiedItem } from "./UnifiedItem";
import { PropertyUpdateHandler } from "./FormProperty/context/context";
import { useQuestionnaireFormContext } from "./context/useQuestionnaireForm";

type PagesContentProps = {
    ssqSsqHeading: SsqSsqHeading;
    post?: Post;
    changePost: (callable: (post: Post) => void) => void;
    allowComments?: boolean;
    unit?: string;
    olapScores: OlapScoresResult | undefined;
    setOlapScores: (olapScores: OlapScoresResult | undefined) => void;
    dataListValueOverrides?: DataListValueOverride[];
};

const parseHashParams = (hash: string): { subheadingId?: number; propertyId?: number } => {
    if (!hash || !hash.startsWith("#")) {
        return {};
    }

    const params = new URLSearchParams(hash.substring(1));
    const subheadingId = params.get("subheadingId");
    const propertyId = params.get("propertyId");

    return {
        subheadingId: subheadingId ? parseInt(subheadingId) : undefined,
        propertyId: propertyId ? parseInt(propertyId) : undefined,
    };
};

export const PagesContent = (props: PagesContentProps) => {
    const {
        ssqSsqHeading,
        post: initialPost,
        changePost,
        allowComments = true,
        unit = "",
        olapScores,
        setOlapScores,
        dataListValueOverrides,
    } = props;

    const theme = useTheme();
    const { lastSelectedVariation } = useEntityScores();
    const { setSaving } = useFormBaseContext();
    const { scores } = useFoodpilotContext();
    const { t } = useTranslation();
    const { setOpenUnitConverterKey } = useQuestionnaireFormContext();
    const snackbar = useSnackbar();
    const updateProperty = useUpdateProperty();
    const deleteProperty = useDeleteProperty();
    const location = useLocation();

    const [post, setPost] = useState<Post | undefined>(undefined);
    const [isUsedInActionPlanWarningModalOpen, setIsUsedInActionPlanWarningModalOpen] = useState(false);
    const [propertyUpdateData, setPropertyUpdateData] = useState<UpdateProperty | undefined>(undefined);
    const propertyUpdateOutcomeRef = useRef<Parameters<PropertyUpdateHandler>[1]>(undefined);
    const [resetKey, setResetKey] = useState(0);

    const [propertyValidationErrors, setPropertyValidationErrors] = useState<ValidationMessagesByKey>({});
    const propertyRefs = useRef<Record<number, HTMLDivElement | null>>({});

    useEffect(() => {
        setPost(initialPost);
    }, [initialPost]);

    useEffect(() => {
        const { propertyId } = parseHashParams(location.hash);
        if (propertyId && propertyRefs.current[propertyId]) {
            setTimeout(() => {
                propertyRefs.current[propertyId]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 100);
        }
    }, [location.hash, post]);

    const displayedScoreId = lastSelectedVariation.scoreId;
    const score = scores?.find((score: Score) => score.id === displayedScoreId);
    const precision = score?.precision ?? DEFAULT_SCORE_PRECISION;

    const ssqSsqHeadingSsqProperties =
        post ? getVisibleSsqSsqHeadingSsqProperties(ssqSsqHeading, post.hiddenElements) : [];
    const visibleSubheadings = post ? filterHeadingsByHiddenElements(ssqSsqHeading.children, post.hiddenElements) : [];

    const mainScore = olapScores ? olapScores[displayedScoreId] : undefined;
    const gradeSubscore = OlapScoresService.getSubscoreWithDisplayMode("grade", mainScore);

    const headingsDimension = OlapScoresService.getDimension("headings", mainScore?.dimensions);
    const headingDimensionValue = OlapScoresService.getDimensionValue(ssqSsqHeading.id, headingsDimension);
    const subheadingsScoresDimension = OlapScoresService.getDimension("subheadings", headingDimensionValue?.dimensions);
    const propertiesScoresDimension = OlapScoresService.getDimension("properties", headingDimensionValue?.dimensions);

    const headingsGradeDimension = OlapScoresService.getDimension("headings", gradeSubscore?.dimensions);
    const headingGradeDimensionValue = OlapScoresService.getDimensionValue(ssqSsqHeading.id, headingsGradeDimension);
    const subheadingsGradeDimension = OlapScoresService.getDimension(
        "subheadings",
        headingGradeDimensionValue?.dimensions,
    );
    const propertiesGradeDimension = OlapScoresService.getDimension(
        "properties",
        headingGradeDimensionValue?.dimensions,
    );

    const setPostOlapScores = (post: Post): void => {
        setOlapScores(post.olapScores);
    };

    const clearValidationErrorsForKey = (key?: string) => {
        if (!key) return;

        setPropertyValidationErrors((previous: ValidationMessagesByKey) => {
            if (!(key in previous)) return previous;
            const { [key]: _removed, ...rest } = previous;
            return rest;
        });
    };

    const applyValidationMessages = (messagesByProperty: ValidationMessagesByKey, propertyKeys: string[]) => {
        setPropertyValidationErrors((previous: ValidationMessagesByKey) => {
            const cleared = { ...previous };
            propertyKeys.forEach((propertyKey) => delete cleared[propertyKey]);
            Object.keys(messagesByProperty).forEach((key) => delete cleared[key]);
            return { ...cleared, ...messagesByProperty };
        });
    };

    const groupValidationMessagesByProperty = (messages: ValidationMessage[]): ValidationMessagesByKey => {
        return messages.reduce((grouped, message) => {
            const targetPropertyId = Number(message.hint?.propertyId ?? 0);
            const targetPosition = Number(message.hint?.position ?? 0);
            const key = message.propertyPath || buildPropertyValidationKey(targetPropertyId, targetPosition);
            return { ...grouped, [key]: [...(grouped[key] ?? []), message] };
        }, {} as ValidationMessagesByKey);
    };

    const propagateValidationMessages = (
        validationMessages: ValidationMessage[],
        propertyId: number,
        propertyValidationKeys: string[],
        ssqVersionStructureId?: number | null,
    ) => {
        const messagesByProperty = groupValidationMessagesByProperty(validationMessages);
        applyValidationMessages(messagesByProperty, propertyValidationKeys);

        // If the parent property has an error add the exact same error for all child elements
        setPropertyValidationErrors((prev) => {
            let updated = { ...prev };

            Object.entries(messagesByProperty).forEach(([key, messages]) => {
                const multivalueMessages = messages.filter((msg) => msg.hint?.isMultivalue === "1");
                if (multivalueMessages.length === 0) return;

                const childKeysToAdd: string[] = [];
                multivalueMessages.forEach((msg) => {
                    const childElements = post?.elements?.filter((element) => element.ssqPropertyId === propertyId);
                    childElements?.forEach((element) => {
                        childKeysToAdd.push(
                            buildPropertyValidationKey(element.ssqPropertyId, element.position, ssqVersionStructureId),
                        );
                    });
                });

                if (childKeysToAdd.length > 0) {
                    const messagesWithEmptyText = messages.map((msg) => ({
                        ...msg,
                        message: "",
                    }));

                    childKeysToAdd.forEach((childKey) => {
                        updated[childKey] = messagesWithEmptyText;
                    });
                }
            });

            return updated;
        });
    };

    const onPropertyUpdate: PropertyUpdateHandler = (data, reportOutcome) => {
        setPropertyUpdateData({ ...data });
        propertyUpdateOutcomeRef.current = reportOutcome;
        snackbar.forceClose();
        const propertyValidationKeys = [
            buildPropertyValidationKey(data.propertyId, data.position),
            buildPropertyValidationKey(data.propertyId, data.position, data.ssqVersionStructureId),
        ].filter((key, index, keys) => keys.indexOf(key) === index);

        if (post?.isUsedInActionPlan && data.doForce !== true && !post?.relatedActionPlansMarkedAsStale) {
            setIsUsedInActionPlanWarningModalOpen(true);
            return;
        } else {
            setIsUsedInActionPlanWarningModalOpen(false);
        }

        setSaving(true);

        const handleSuccess = (response: Awaited<ReturnType<typeof updateProperty.mutateAsync>>) => {
            changePost((post: Post): void => {
                setPost(post);
                setPostOlapScores(post);
            });
            reportOutcome?.({ saved: true });
            const validationMessages = extractValidationMessages(response.data?.violations, "warning");

            if (validationMessages.length > 0) {
                propagateValidationMessages(
                    validationMessages,
                    data.propertyId,
                    propertyValidationKeys,
                    data.ssqVersionStructureId,
                );
            } else {
                propertyValidationKeys.forEach((key) => clearValidationErrorsForKey(key));
                const validationKeysToInvalidate = response.data?.validationKeysToInvalidate ?? [];
                validationKeysToInvalidate.forEach((key: string) => clearValidationErrorsForKey(key));
            }
        };

        const handleError = (error: unknown) => {
            if (!isAxiosError(error)) {
                reportOutcome?.({ saved: false, messages: [] });
                return;
            }

            const statusCode = error.response?.status;

            const calculationFailure = classifyCalculationFailure(error);
            if (calculationFailure.kind !== "unknown") {
                const message = calculationFailureMessage(calculationFailure, t);
                reportOutcome?.({ saved: false, messages: [message] });
                snackbar.setSnackbarProps({
                    message,
                    type: "error",
                    forceAutoHideInMilisecond: 10000,
                });
                return;
            }

            if (statusCode === 422) {
                const validationMessages = extractValidationMessages(error.response?.data, "error");
                if (validationMessages.length > 0) {
                    reportOutcome?.({
                        saved: false,
                        messages: validationMessages.map((validationMessage) => validationMessage.message),
                    });
                    propagateValidationMessages(
                        validationMessages,
                        data.propertyId,
                        propertyValidationKeys,
                        data.ssqVersionStructureId,
                    );

                    snackbar.setSnackbarProps({
                        message: (
                            <Stack component="span" gap="4px">
                                {validationMessages.map((validationMessage, index) => (
                                    <span key={`validation-error-${index}`}>{validationMessage.message}</span>
                                ))}
                            </Stack>
                        ),
                        type: "error",
                    });
                    return;
                }
            }

            reportOutcome?.({ saved: false, messages: statusCode !== 401 ? [t("ssq.form.error")] : [] });
            if (statusCode !== 401) {
                snackbar.setSnackbarProps({
                    message: t("ssq.form.error"),
                    type: "error",
                });
            }
        };

        void updateProperty
            .mutateAsync(data)
            .then(handleSuccess, handleError)
            .finally(() => {
                setSaving(false);
            });
    };

    const onPropertyDelete = (data: DeleteProperty) => {
        setSaving(true);
        snackbar.forceClose();

        deleteProperty.mutate(data, {
            onSuccess: (response) => {
                changePost((post: Post): void => {
                    setPost(post);
                    setPostOlapScores(post);
                });
            },
            onError: async (error) => {
                if (isAxiosError(error)) {
                    const isForbidden = error.response?.status === 401;
                    if (isForbidden === false) {
                        snackbar.setSnackbarProps({
                            message: t("ssq.form.error"),
                            type: "error",
                        });
                    }
                }
            },
            onSettled: () => {
                setSaving(false);
            },
        });
    };

    const unifiedItems = useMemo(
        () => unifyHeadingsAndProperties(visibleSubheadings, ssqSsqHeadingSsqProperties),
        [visibleSubheadings, ssqSsqHeadingSsqProperties],
    );

    return (
        <Stack gap="28px" alignItems="stretch">
            <HeadingHelperTextBox value={ssqSsqHeading.helperText} />
            <PostUsedInActionPlanModal
                isOpen={isUsedInActionPlanWarningModalOpen}
                onClose={() => {
                    setIsUsedInActionPlanWarningModalOpen(false);
                    setOpenUnitConverterKey(null);
                    setResetKey((prev) => prev + 1);
                }}
                onUpdate={() => {
                    if (propertyUpdateData) {
                        onPropertyUpdate({ ...propertyUpdateData, doForce: true }, propertyUpdateOutcomeRef.current);
                    }
                }}
            />

            <Stack gap="16px">
                {unifiedItems.map((item) => (
                    <UnifiedItem
                        key={`${item.type}-${item.data.id}-${resetKey}`}
                        item={item}
                        ssqVersionStructureId={ssqSsqHeading.ssqVersionStructureId}
                        post={post}
                        changePost={changePost}
                        onPropertyUpdate={onPropertyUpdate}
                        onPropertyDelete={onPropertyDelete}
                        setPost={setPost}
                        scoreDimensions={{
                            subheadings: subheadingsScoresDimension,
                            properties: propertiesScoresDimension,
                        }}
                        gradeDimensions={{
                            subheadings: subheadingsGradeDimension,
                            properties: propertiesGradeDimension,
                        }}
                        precision={precision}
                        unit={unit}
                        validationErrors={propertyValidationErrors}
                        dataListValueOverrides={dataListValueOverrides}
                        propertyRefs={propertyRefs}
                    />
                ))}
            </Stack>
        </Stack>
    );
};
